import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderOpen, FileText, BookOpen, ClipboardList, PenTool, HelpCircle, Upload, Search, Plus, Bot, Loader2, Save, Share2, Award, ChevronRight, Download, Sparkles, Lightbulb, Play, List, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = [
  { bn: 'বাংলা', en: 'Bangla' }, { bn: 'English', en: 'English' }, { bn: 'গণিত', en: 'Mathematics' },
  { bn: 'বিজ্ঞান', en: 'Science' }, { bn: 'সমাজবিজ্ঞান', en: 'Social Science' }, { bn: 'তথ্য প্রযুক্তি', en: 'ICT' },
];
const RESOURCE_TYPES = [
  { id: 'lesson_plan', en: 'Lesson Plans', bn: 'পাঠ পরিকল্পনা', icon: BookOpen },
  { id: 'worksheet', en: 'Worksheets', bn: 'ওয়ার্কশীট', icon: ClipboardList },
  { id: 'template', en: 'Templates', bn: 'টেমপ্লেট', icon: FileText },
  { id: 'study_material', en: 'Study Material', bn: 'অধ্যয়ন উপকরণ', icon: BookOpen },
  { id: 'revision', en: 'Revision Material', bn: 'রিভিশন উপকরণ', icon: PenTool },
  { id: 'question_paper', en: 'Question Papers', bn: 'প্রশ্নপত্র', icon: HelpCircle },
];

export default function ResourceHub() {
  const { t, lang, currentTeacher, currentSchool } = useApp();
  const [view, setView] = useState('home');
  const [resources, setResources] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [form, setForm] = useState({ grade: '', subject: '', chapter: '', objective: '' });
  const [generatedPlan, setGeneratedPlan] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saved, setSaved] = useState(false);
  const [demoStep, setDemoStep] = useState(0);

  useEffect(() => {
    if (currentTeacher) {
      axios.get(`${API}/lesson-plans`, { params: { teacher_id: currentTeacher.teacher_id } })
        .then(r => setResources(r.data)).catch(() => {});
    }
  }, [currentTeacher, saved]);

  const generate = async () => {
    if (!form.grade || !form.subject) { toast.error(lang === 'bn' ? 'শ্রেণি ও বিষয় নির্বাচন করুন' : 'Select grade and subject'); return; }
    setGenerating(true); setGeneratedPlan(''); setSaved(false);
    try {
      const res = await axios.post(`${API}/lesson-plans/generate`, {
        teacher_id: currentTeacher?.teacher_id || 'demo', grade: parseInt(form.grade), subject: form.subject, chapter: form.chapter || null, learning_objective: form.objective || null,
      });
      setGeneratedPlan(res.data.plan || res.data.content || '');
    } catch (e) { toast.error(lang === 'bn' ? 'AI সেবা সাময়িকভাবে অনুপলব্ধ' : 'AI service temporarily unavailable'); }
    setGenerating(false);
  };

  const savePlan = async () => {
    if (!generatedPlan) return;
    await axios.post(`${API}/lesson-plans`, {
      teacher_id: currentTeacher?.teacher_id || 'demo', school_id: currentSchool?.school_id || 'demo',
      title_bangla: `${form.subject} - Grade ${form.grade}`, grade: parseInt(form.grade), subject: form.subject, content: generatedPlan, ai_generated: true,
    });
    toast.success(lang === 'bn' ? 'সংরক্ষিত' : 'Saved'); setSaved(true);
  };

  const DEMO_STEPS = [
    { en: 'Select Grade & Subject', bn: 'শ্রেণি ও বিষয় নির্বাচন', desc_en: 'Choose grade (1-12) and subject. AI aligns with NCTB curriculum.', desc_bn: 'শ্রেণি ও বিষয় নির্বাচন করুন। AI NCTB পাঠ্যক্রমের সাথে মেলায়।' },
    { en: 'Add Details', bn: 'বিস্তারিত যোগ করুন', desc_en: 'Add chapter name and learning objective for better output.', desc_bn: 'আরো ভালো ফলাফলের জন্য অধ্যায় ও শিখন উদ্দেশ্য যোগ করুন।' },
    { en: 'Generate with AI', bn: 'AI দিয়ে তৈরি করুন', desc_en: 'AI creates: Objectives, Materials, Activities, Assessment, Homework.', desc_bn: 'AI তৈরি করে: উদ্দেশ্য, উপকরণ, কার্যক্রম, মূল্যায়ন, বাড়ির কাজ।' },
    { en: 'Review & Edit', bn: 'পর্যালোচনা ও সম্পাদনা', desc_en: 'Review the draft, make changes, add your ideas.', desc_bn: 'খসড়া পর্যালোচনা করুন, পরিবর্তন করুন, আপনার ধারণা যোগ করুন।' },
    { en: 'Save & Share', bn: 'সংরক্ষণ ও শেয়ার', desc_en: 'Save to My Resources. Submit to Editorial Board for adoption.', desc_bn: 'আমার রিসোর্সে সংরক্ষণ করুন। গ্রহণের জন্য সম্পাদনা বোর্ডে জমা দিন।' },
  ];

  // HOME
  if (view === 'home') return (
    <div className="space-y-6" data-testid="resource-hub-home">
      <div>
        <h2 style={{ fontSize: 'var(--text-title2)', fontWeight: 700 }}>{lang === 'bn' ? 'রিসোর্স হাব' : 'Resource Hub'}</h2>
        <p style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Resource Hub' : 'রিসোর্স হাব'}</p>
        <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)' }}>
          {lang === 'bn' ? 'পাঠ পরিকল্পনা, ওয়ার্কশীট, টেমপ্লেট, অধ্যয়ন উপকরণ, প্রশ্নপত্র' : 'Lesson Plans, Worksheets, Templates, Study Material, Question Papers'}
        </p>
      </div>
      {/* Resource type cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {RESOURCE_TYPES.map(rt => { const Icon = rt.icon; return (
          <button key={rt.id} onClick={() => { setFilterType(rt.id); setView('browse'); }}
            className="card-surface text-left" style={{ padding: 'var(--space-5)', cursor: 'pointer' }}>
            <Icon size={22} style={{ color: 'var(--color-accent)', marginBottom: 'var(--space-3)' }} />
            <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 600 }}>{lang === 'bn' ? rt.bn : rt.en}</div>
            <div style={{ fontSize: '10px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? rt.en : rt.bn}</div>
          </button>
        ); })}
      </div>
      {/* Action buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => setView('browse')} className="card-surface" style={{ padding: 'var(--space-6)', cursor: 'pointer', textAlign: 'left' }}>
          <FileText size={24} style={{ color: 'var(--color-accent)', marginBottom: 'var(--space-3)' }} />
          <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'রিসোর্স দেখুন' : 'Browse Resources'}</div>
          <div style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Browse Resources' : 'রিসোর্স দেখুন'}</div>
          <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-accent)', marginTop: 'var(--space-3)' }}>{resources.length} {lang === 'bn' ? 'টি রিসোর্স' : 'resources'} →</div>
        </button>
        <button onClick={() => setView('create')} className="card-surface" style={{ padding: 'var(--space-6)', cursor: 'pointer', textAlign: 'left', borderTop: '3px solid var(--color-accent)' }}>
          <Sparkles size={24} style={{ color: 'var(--color-red)', marginBottom: 'var(--space-3)' }} />
          <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'নতুন রিসোর্স তৈরি' : 'Create New Resource'}</div>
          <div style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Create New Resource' : 'নতুন রিসোর্স তৈরি'}</div>
          <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-red)', marginTop: 'var(--space-3)' }}><Sparkles size={12} style={{ display: 'inline' }} /> AI-powered</div>
        </button>
      </div>
      {/* AI Demo Tutorial */}
      <div className="card-surface" style={{ borderLeft: '4px solid var(--color-red)', padding: 'var(--space-5)' }}>
        <div className="flex items-start gap-4">
          <Lightbulb size={22} style={{ color: 'var(--color-red)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--text-body)', fontWeight: 600 }}>{lang === 'bn' ? 'AI দিয়ে রিসোর্স তৈরি — ডেমো' : 'How to Use AI to Create Resources — Demo'}</div>
            <div style={{ fontSize: '10px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Demo Tutorial' : 'ডেমো টিউটোরিয়াল'}</div>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)', lineHeight: 1.6 }}>
              {lang === 'bn' ? '৫ ধাপে শিখুন কিভাবে AI ব্যবহার করে পাঠ পরিকল্পনা তৈরি করতে হয়।' : 'Learn how to create lesson plans with AI in 5 easy steps.'}
            </p>
            <button onClick={() => { setView('demo'); setDemoStep(0); }} className="btn btn-sm" style={{ background: 'var(--color-red)', color: 'white', marginTop: 'var(--space-3)' }}>
              <Play size={14} /> {lang === 'bn' ? 'ডেমো দেখুন' : 'Watch Demo'}
            </button>
          </div>
        </div>
      </div>
      {/* Submit to Editorial Board */}
      <div className="card-surface" style={{ padding: 'var(--space-5)' }}>
        <div className="flex items-center gap-3">
          <Upload size={20} style={{ color: 'var(--color-accent)' }} />
          <div>
            <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 600 }}>{lang === 'bn' ? 'সম্পাদনা বোর্ড / NAEM-এ জমা দিন' : 'Submit to Editorial Board / NAEM'}</div>
            <div style={{ fontSize: '10px', color: 'var(--color-label-tertiary)', marginTop: 1 }}>{lang === 'bn' ? 'Submit to Editorial Board / NAEM' : 'সম্পাদনা বোর্ড / NAEM-এ জমা দিন'}</div>
            <p style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-1)' }}>
              {lang === 'bn' ? 'আপনার তৈরি রিসোর্স জেলা বা জাতীয় পর্যায়ে গ্রহণের জন্য জমা দিন। লিডারবোর্ডে পয়েন্ট অর্জন করুন। সম্প্রদায় হাবে শেয়ার করুন এবং আলোচনা করুন।' : 'Submit your resources for district or national adoption. Earn leaderboard points for every submission. Share to Community Hub for comments and open discussion.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // DEMO
  if (view === 'demo') { const step = DEMO_STEPS[demoStep]; return (
    <div className="space-y-6" data-testid="resource-hub-demo">
      <button onClick={() => setView('home')} style={{ color: 'var(--color-label-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>← {lang === 'bn' ? 'ফিরে যান' : 'Back'}</button>
      <div className="progress-track"><div className="progress-fill" style={{ width: `${((demoStep + 1) / DEMO_STEPS.length) * 100}%` }} /></div>
      <div className="card-surface" style={{ padding: 'var(--space-10)', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--color-accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-6)' }}>
          <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-accent)' }}>{demoStep + 1}</span>
        </div>
        <h2 style={{ fontSize: 'var(--text-title2)', fontWeight: 700 }}>{lang === 'bn' ? step.bn : step.en}</h2>
        <p style={{ fontSize: 'var(--text-body)', color: 'var(--color-label-secondary)', lineHeight: 1.7, maxWidth: 500, margin: 'var(--space-4) auto 0' }}>{lang === 'bn' ? step.desc_bn : step.desc_en}</p>
      </div>
      <div className="flex items-center justify-between">
        <button onClick={() => setDemoStep(Math.max(0, demoStep - 1))} className="btn btn-secondary btn-sm" disabled={demoStep === 0}>← {lang === 'bn' ? 'আগের' : 'Previous'}</button>
        {demoStep < DEMO_STEPS.length - 1
          ? <button onClick={() => setDemoStep(demoStep + 1)} className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}>{lang === 'bn' ? 'পরবর্তী' : 'Next'} →</button>
          : <button onClick={() => setView('create')} className="btn btn-sm" style={{ background: 'var(--color-red)', color: 'white' }}><Sparkles size={14} /> {lang === 'bn' ? 'এখন তৈরি করুন' : 'Create Now'}</button>
        }
      </div>
    </div>
  ); }

  // BROWSE
  if (view === 'browse') return (
    <div className="space-y-4" data-testid="resource-hub-browse">
      <div className="flex items-center justify-between">
        <button onClick={() => setView('home')} style={{ color: 'var(--color-label-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>← {lang === 'bn' ? 'ফিরে যান' : 'Back'}</button>
        <button onClick={() => setView('create')} className="btn btn-sm" style={{ background: 'var(--color-accent)', color: 'white' }}><Plus size={14} /> {lang === 'bn' ? 'নতুন' : 'New'}</button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterType('all')} className="btn btn-sm" style={{ background: filterType === 'all' ? 'var(--color-accent)' : 'var(--color-surface-2)', color: filterType === 'all' ? 'white' : 'var(--color-label-secondary)' }}>{lang === 'bn' ? 'সব' : 'All'}</button>
        {RESOURCE_TYPES.map(rt => (
          <button key={rt.id} onClick={() => setFilterType(rt.id)} className="btn btn-sm" style={{ background: filterType === rt.id ? 'var(--color-accent)' : 'var(--color-surface-2)', color: filterType === rt.id ? 'white' : 'var(--color-label-secondary)', fontSize: '12px' }}>{lang === 'bn' ? rt.bn : rt.en}</button>
        ))}
      </div>
      {resources.length === 0 ? (
        <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
          <FolderOpen size={40} style={{ color: 'var(--color-label-tertiary)', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--color-label-secondary)' }}>{lang === 'bn' ? 'কোনো রিসোর্স নেই' : 'No resources yet'}</p>
        </div>
      ) : (
        <div className="card-surface" style={{ padding: 0, overflow: 'hidden' }}>
          {resources.map(p => (
            <div key={p.plan_id} className="list-row" style={{ padding: 'var(--space-4) var(--space-5)' }}>
              <div className="list-row-icon"><FileText size={16} style={{ color: 'var(--color-accent)' }} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500 }}>{p.title_bangla}</div>
                <div style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>
                  {p.subject} | Grade {p.grade} {p.ai_generated && <span style={{ color: 'var(--color-warning)' }}> AI</span>}
                  {p.shared_to_school && <span> <Share2 size={10} style={{ display: 'inline', color: 'var(--color-accent)' }} /></span>}
                </div>
              </div>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-quarternary)' }}>{new Date(p.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // CREATE (AI Generator)
  return (
    <div className="space-y-4" data-testid="resource-hub-create">
      <button onClick={() => setView('home')} style={{ color: 'var(--color-label-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>← {lang === 'bn' ? 'ফিরে যান' : 'Back'}</button>
      <div>
        <h2 style={{ fontSize: 'var(--text-title3)', fontWeight: 600 }}>{lang === 'bn' ? 'নতুন রিসোর্স তৈরি' : 'Create New Resource'}</h2>
        <div style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Create New Resource' : 'নতুন রিসোর্স তৈরি'}</div>
      </div>
      <div className="card-surface space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>{lang === 'bn' ? 'শ্রেণি' : 'Grade'}</label>
            <select value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} className="input-filled">
              <option value="">--</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-footnote)', fontWeight: 500, color: 'var(--color-label-secondary)', display: 'block', marginBottom: 'var(--space-2)' }}>{lang === 'bn' ? 'বিষয়' : 'Subject'}</label>
            <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="input-filled">
              <option value="">--</option>
              {SUBJECTS.map(s => <option key={s.bn} value={s.bn}>{lang === 'bn' ? s.bn : s.en}</option>)}
            </select>
          </div>
        </div>
        <input type="text" value={form.chapter} onChange={e => setForm({ ...form, chapter: e.target.value })} className="input-filled" placeholder={lang === 'bn' ? 'অধ্যায়' : 'Chapter (optional)'} />
        <textarea value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })} className="input-filled" rows={2} placeholder={lang === 'bn' ? 'শিখন উদ্দেশ্য' : 'Learning objective (optional)'} />
        <button onClick={generate} disabled={generating} className="btn w-full" style={{ background: 'var(--color-accent)', color: 'white' }}>
          {generating ? <><Loader2 size={18} className="animate-spin" /> {lang === 'bn' ? 'তৈরি হচ্ছে...' : 'Generating...'}</> : <><Bot size={18} /> {lang === 'bn' ? 'AI দিয়ে তৈরি করুন' : 'Generate with AI'}</>}
        </button>
      </div>
      {generatedPlan && (
        <div className="card-surface space-y-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-sm)', borderLeft: '4px solid var(--color-warning)' }}>
            <Bot size={16} style={{ color: 'var(--color-warning)' }} />
            <span style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-warning)', fontWeight: 500 }}>{lang === 'bn' ? 'AI সহায়তায় তৈরি — শিক্ষক পর্যালোচনা করুন' : 'AI draft — please review'}</span>
          </div>
          <div style={{ fontSize: 'var(--text-subhead)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{generatedPlan}</div>
          {!saved ? (
            <div className="flex gap-3">
              <button onClick={savePlan} className="btn flex-1" style={{ background: 'var(--color-accent)', color: 'white' }}><Save size={16} /> {lang === 'bn' ? 'সংরক্ষণ' : 'Save'}</button>
              <button className="btn flex-1 btn-secondary"><Upload size={16} /> {lang === 'bn' ? 'NAEM-এ জমা দিন' : 'Submit to NAEM'}</button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--space-4)', background: 'var(--color-success-bg)', borderRadius: 'var(--radius-sm)', color: 'var(--color-success)' }}>{lang === 'bn' ? 'সংরক্ষিত' : 'Saved!'}</div>
          )}
        </div>
      )}
    </div>
  );
}
